const LOADING_DURATION_MS = 1400;
const UI_REVEAL_DURATION_MS = 1950;
const PRODUCT_REVEAL_DELAY = 0.28;
const PRELOAD_TIMEOUT_MS = 8000;

// 最新ワイドKV: hex 152px。1・2・4段は横GAP 19px、他段は密着
// 座標は MV 486:6628 の absolute。1440x900 へは bg_img オフセット分ずらす
const KV_ART_W = 1440;
const KV_ART_H = 900;
const KV_ART_X = 254.828125;
const KV_ART_Y = 42.146484375;
const HEX_TILE = 152;
const HEX_GAP = 19;
const HEX_STEP_GAP = HEX_TILE + HEX_GAP;
const HEX_STEP_FLUSH = HEX_TILE;
const toKvPercent = (x, y) => [
  ((x - KV_ART_X) / KV_ART_W) * 100,
  ((y - KV_ART_Y) / KV_ART_H) * 100,
];

// 各クラスター先頭は既存 x。以降はその段の step
const HEX_CLUSTERS = [
  { y: 87.642578125, x: 376.1312255859375, count: 7, step: HEX_STEP_GAP, kvRow: 2 },
  { y: 243.4423828125, x: 264.1976623535156, count: 3, step: HEX_STEP_FLUSH, kvRow: 3 },
  { y: 243.4423828125, x: 1198.9976806640625, count: 3, step: HEX_STEP_FLUSH, kvRow: 3 },
  { y: 399.2421875, x: 345.73284912109375, count: 2, step: HEX_STEP_GAP, kvRow: 4 },
  { y: 399.2421875, x: 1219.7333984375, count: 3, step: HEX_STEP_GAP, kvRow: 4 },
  { y: 554.9033203125, x: 254.69769287109375, count: 3, step: HEX_STEP_FLUSH, kvRow: 5 },
  { y: 554.9033203125, x: 1185.69775390625, count: 3, step: HEX_STEP_FLUSH, kvRow: 5 },
  { y: 710.703125, x: 342.9976806640625, count: 8, step: HEX_STEP_FLUSH, kvRow: 6 },
];

const BASE_HEX_POSITIONS = HEX_CLUSTERS.flatMap(({ x, y, count, step }) =>
  Array.from({ length: count }, (_, i) => [
    ((x + i * step - KV_ART_X) / KV_ART_W) * 100,
    ((y - KV_ART_Y) / KV_ART_H) * 100,
  ]),
);

// HEX_POSITIONS と同じ順。item-01=義足 / item-12=青緑円形 / item-26=女性の顔
const HEX_ITEM_MAP = [
  2, 3, 4, 5, 6, 7, 1, 8, 9, 10, 11, 12, 13, 15, 14, 17, 18, 19, 21, 22, 23, 24,
  25, 26, 27, 16, 29, 30, 31, 32, 33, 34,
];

// 上下の新規段だけ Figma 2002:161 を写す（基準は旧1段目先頭）
const FIGMA_OFFSET_X =
  376.1312255859375 - (287.05092797387624 + 349.5999755859375);
const FIGMA_OFFSET_Y = 87.642578125 - 247.6414296971052;
const FIGMA_ROWS = {
  r1: [123.126953125, 92.1474609375],
  r7: [228.4658203125, 1028.146484375],
};

const figmaSlot = (row, x, y, id, shape) => ({
  id,
  shape,
  pos: toKvPercent(row[0] + x + FIGMA_OFFSET_X, row[1] + y + FIGMA_OFFSET_Y),
});

const [HEX_R2, HEX_R3L, HEX_R3R, HEX_R4L, HEX_R4R, HEX_R5L, HEX_R5R, HEX_R6] =
  HEX_CLUSTERS;

// 既存クラスターの step 続き。index は先頭からのマス番号（負=左、count以上=右）
const BASE_HEX_ROWS = HEX_CLUSTERS.flatMap(({ count, kvRow }) =>
  Array.from({ length: count }, () => kvRow),
);

const clusterSlot = (cluster, index, id, shape, xGroup) => ({
  id,
  shape,
  axis: "x",
  xGroup,
  kvRow: cluster.kvRow,
  pos: toKvPercent(cluster.x + index * cluster.step, cluster.y),
});

const ROW1_START_X = 269.2652587890625;
const ROW_Y_MID = 16;
const row1Slot = (i, id, shape) => ({
  ...figmaSlot(FIGMA_ROWS.r1, ROW1_START_X + i * HEX_STEP_GAP, ROW_Y_MID, id, shape),
  axis: i === 0 || i === 9 ? "xy" : "y",
  xGroup: i === 0 ? "L2" : i === 9 ? "R2" : undefined,
  kvRow: 1,
});
const row7Slot = (i, id, shape) => ({
  ...figmaSlot(
    FIGMA_ROWS.r7,
    HEX_STEP_FLUSH + i * HEX_STEP_FLUSH,
    ROW_Y_MID,
    id,
    shape,
  ),
  axis: i === 0 || i === 10 ? "xy" : "y",
  xGroup: i === 0 ? "L2" : i === 10 ? "R2" : undefined,
  kvRow: 7,
});

// 外周 36 スロット。横出現は左右セットの3段階（内側 L1+R1 → 中 L2+R2 → 外側 L3+R3）
const WIDE_HEX_ITEMS = [
  row1Slot(0, 35),
  row1Slot(1, 30),
  row1Slot(2, 37),
  row1Slot(3, 38, "hex"),
  row1Slot(4, 39),
  row1Slot(5, 40),
  row1Slot(6, 41, "hex"),
  row1Slot(7, 42),
  row1Slot(8, 43, "circle"),
  row1Slot(9, 44),
  clusterSlot(HEX_R2, -2, 45, undefined, "L3"),
  clusterSlot(HEX_R2, -1, 46, undefined, "L1"),
  clusterSlot(HEX_R2, 7, 47, undefined, "R1"),
  clusterSlot(HEX_R2, 8, 48, undefined, "R3"),
  clusterSlot(HEX_R3L, -1, 49, undefined, "L2"),
  clusterSlot(HEX_R3R, 3, 50, undefined, "R2"),
  clusterSlot(HEX_R4L, -2, 52, undefined, "L3"),
  clusterSlot(HEX_R4L, -1, 53, undefined, "L1"),
  clusterSlot(HEX_R4R, 3, 51, undefined, "R3"),
  clusterSlot(HEX_R5L, -1, 54, undefined, "L2"),
  clusterSlot(HEX_R5R, 3, 55, undefined, "R2"),
  clusterSlot(HEX_R6, -2, 56, undefined, "L3"),
  clusterSlot(HEX_R6, -1, 57, undefined, "L1"),
  clusterSlot(HEX_R6, 8, 58, undefined, "R1"),
  clusterSlot(HEX_R6, 9, 59, undefined, "R3"),
  row7Slot(0, 60),
  row7Slot(1, 61),
  row7Slot(2, 63),
  row7Slot(3, 62, "circle"),
  row7Slot(4, 64, "hex"),
  row7Slot(5, 65),
  row7Slot(6, 66),
  row7Slot(7, 67, "circle"),
  row7Slot(8, 68),
  row7Slot(9, 69),
  row7Slot(10, 70),
];

const HEX_ITEMS = [
  ...BASE_HEX_POSITIONS.map((pos, index) => ({
    pos,
    id: HEX_ITEM_MAP[index],
    kvRow: BASE_HEX_ROWS[index],
  })),
  ...WIDE_HEX_ITEMS,
];

const HEX_CENTER = [50, 47];
const HEX_ITEM_COUNT = HEX_ITEMS.length;

const HEX_SVG_IDS = new Set([
  3, 7, 8, 17, 25, 30, 39, 50, 52, 54, 55, 58, 63, 66,
]);

const IDLE_PRESETS = {
  default: {
    delayMul: 0.18,
    durBase: 4.6,
    durMul: 0.37,
    yBase: 4,
    yMod: 7,
  },
  mid: {
    delayMul: 0.205,
    durBase: 6.5,
    durMul: 0.54,
    yBase: 3,
    yMod: 5,
  },
  slow: {
    delayMul: 0.23,
    durBase: 8,
    durMul: 0.75,
    yBase: 3,
    yMod: 4,
  },
};

const motionItemSrc = (motionBase, id) => {
  const itemNo = String(id).padStart(2, "0");
  const ext = HEX_SVG_IDS.has(id) ? "svg" : "webp";
  return `${motionBase}item-${itemNo}.${ext}`;
};

const buildHexField = (field, motionBase, idle) => {
  if (!field || field.childElementCount) return;

  HEX_ITEMS.forEach(({ pos: [left, top], id, shape, axis, xGroup, kvRow }, index) => {
    const dx = left - HEX_CENTER[0];
    const dy = top - HEX_CENTER[1];
    const distancePx = Math.hypot(dx * 14.4, dy * 9);
    const rippleProgress = Math.min(
      1,
      Math.max(0, (distancePx - 58) / 640),
    );
    const rippleArrival = PRODUCT_REVEAL_DELAY + rippleProgress * 0.9;
    const idleDelay = ((index * 17) % 13) * idle.delayMul;
    const idleDur = idle.durBase + ((index * 5) % 9) * idle.durMul;
    const idleY = idle.yBase + ((index * 9) % idle.yMod);

    const hex = document.createElement("div");
    hex.className = shape ? `kv-hex kv-hex--${shape}` : "kv-hex";
    if (kvRow) hex.dataset.kvRow = String(kvRow);
    hex.dataset.side = left < HEX_CENTER[0] ? "l" : "r";
    if (axis) {
      hex.classList.add("kv-hex--wide");
      hex.dataset.wideAxis = axis;
      if (xGroup) hex.dataset.xGroup = xGroup;
    }
    hex.style.cssText = [
      `--left:${left}%`,
      `--top:${top}%`,
      `--wave-out:${rippleArrival}s`,
      `--idle-delay:${idleDelay}s`,
      `--idle-dur:${idleDur}s`,
      `--idle-y:${idleY}px`,
    ].join(";");

    const shell = document.createElement("div");
    shell.className = "kv-hex__shell";
    const img = document.createElement("img");
    img.src = motionItemSrc(motionBase, id);
    img.alt = "";
    img.decoding = "async";
    shell.appendChild(img);

    if (axis) {
      const gate = document.createElement("div");
      gate.className = "kv-hex__gate";
      gate.appendChild(shell);
      hex.appendChild(gate);
    } else {
      hex.appendChild(shell);
    }
    field.appendChild(hex);
  });
};

const FRAME_SAFE_PAD = 0;
const HEX_GATE_INSET = 10;
// 金枠に被る／わずかに食み出す程度は出現させる（上下段の余白対策）
const FRAME_OVERLAP_SLACK = 64;
const X_STAGE_PAIRS = [
  ["L1", "R1"],
  ["L2", "R2"],
  ["L3", "R3"],
];

const getFrameInnerRect = (frame) => {
  const box = frame.getBoundingClientRect();
  const cs = getComputedStyle(frame);
  const fallback = window.matchMedia("(max-width: 768px)").matches ? 20 : 36;
  const top = parseFloat(cs.borderTopWidth) || fallback;
  const right = parseFloat(cs.borderRightWidth) || fallback;
  const bottom = parseFloat(cs.borderBottomWidth) || fallback;
  const left = parseFloat(cs.borderLeftWidth) || fallback;
  return {
    top: box.top + top + FRAME_SAFE_PAD,
    right: box.right - right - FRAME_SAFE_PAD,
    bottom: box.bottom - bottom - FRAME_SAFE_PAD,
    left: box.left + left + FRAME_SAFE_PAD,
  };
};

const getHexLayoutBox = (hex, fieldRect) => {
  const inset = HEX_GATE_INSET;
  const left = fieldRect.left + hex.offsetLeft + inset;
  const top = fieldRect.top + hex.offsetTop + inset;
  const width = Math.max(0, hex.offsetWidth - inset * 2);
  const height = Math.max(0, hex.offsetHeight - inset * 2);
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
  };
};

const isInsideFrameAxis = (start, end, innerStart, innerEnd) =>
  start >= innerStart - FRAME_OVERLAP_SLACK &&
  end <= innerEnd + FRAME_OVERLAP_SLACK;

export const syncWideHexVisibility = (kvRoot = document.querySelector(".js-kv")) => {
  const field = kvRoot?.querySelector(".kv__field");
  const frame = document.querySelector(".page-frame");
  if (!field || !frame) return;

  const inner = getFrameInnerRect(frame);
  const fieldRect = field.getBoundingClientRect();
  const hexes = [...field.querySelectorAll(".kv-hex--wide")];
  const groupInX = {};
  let groupInY = true;
  hexes.forEach((hex) => {
    const box = getHexLayoutBox(hex, fieldRect);
    const group = hex.dataset.xGroup;
    if (group) {
      const inX = isInsideFrameAxis(box.left, box.right, inner.left, inner.right);
      groupInX[group] = group in groupInX ? groupInX[group] && inX : inX;
    }
    const axis = hex.dataset.wideAxis;
    if (axis === "y" || axis === "xy") {
      const inY = isInsideFrameAxis(box.top, box.bottom, inner.top, inner.bottom);
      groupInY = groupInY && inY;
    }
  });

  X_STAGE_PAIRS.forEach(([left, right]) => {
    const both = Boolean(groupInX[left]) && Boolean(groupInX[right]);
    groupInX[left] = both;
    groupInX[right] = both;
  });

  const isSpLayout = window.matchMedia("(max-width: 768px)").matches;

  hexes.forEach((hex) => {
    const box = getHexLayoutBox(hex, fieldRect);
    const axis = hex.dataset.wideAxis;
    const group = hex.dataset.xGroup;
    const inX = group
      ? Boolean(groupInX[group])
      : isInsideFrameAxis(box.left, box.right, inner.left, inner.right);
    let inside =
      axis === "x" ? inX : axis === "xy" ? inX && groupInY : groupInY;
    // SP: 上下の新規段は枠判定でまとめて隠れないよう常に出す（トリミングは sticky 側）
    if (isSpLayout) {
      const row = hex.dataset.kvRow;
      if (row === "1" || row === "7") inside = true;
    }
    hex.classList.toggle("is-wide-in", inside);
  });
};

const preloadMotionImages = (motionBase, itemIds, timeoutMs) =>
  new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const ids = [...new Set(itemIds)];
    const timer = window.setTimeout(finish, timeoutMs);
    let remaining = ids.length;

    ids.forEach((id) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        remaining -= 1;
        if (remaining <= 0) {
          window.clearTimeout(timer);
          finish();
        }
      };
      img.src = motionItemSrc(motionBase, id);
    });
  });

const setKvPhaseClasses = (kvRoot, phase) => {
  const phases = ["is-kv-loading", "is-kv-revealing", "is-kv-ready"];
  phases.forEach((name) => {
    const on = name === phase;
    kvRoot.classList.toggle(name, on);
    document.body.classList.toggle(name, on);
  });
};

export const KV_INTRO_PLAYED_KEY = "inoac:kv:introPlayed";

export const hasKvIntroPlayed = () => {
  try {
    return sessionStorage.getItem(KV_INTRO_PLAYED_KEY) === "1";
  } catch {
    return false;
  }
};

export const markKvIntroPlayed = () => {
  try {
    sessionStorage.setItem(KV_INTRO_PLAYED_KEY, "1");
  } catch {
    /* ignore quota / private mode */
  }
};

export const shouldSkipKvOpening = () => {
  if (document.documentElement.classList.contains("is-kv-skip-opening")) {
    return true;
  }
  return hasKvIntroPlayed();
};

export const initKvLoading = (kvRoot, { onReady } = {}) => {
  if (!kvRoot) {
    onReady?.();
    return () => {};
  }

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const field = kvRoot.querySelector(".kv__field");
  const loader = kvRoot.querySelector(".kv__loader");
  const fallbackBg = kvRoot.querySelector(".kv__bg");
  const motionBase = kvRoot.dataset.motionBase || "/assets/img/kv/motion/";
  const idle = IDLE_PRESETS[kvRoot.dataset.kvIdle] || IDLE_PRESETS.default;

  const enableFallback = () => {
    kvRoot.classList.add("is-kv-fallback");
    fallbackBg?.classList.add("kv__bg--fallback");
  };

  if (kvRoot.dataset.kvOpening !== "true") {
    enableFallback();
    setKvPhaseClasses(kvRoot, "is-kv-ready");
    field?.setAttribute("aria-hidden", "true");
    onReady?.();
    return () => {};
  }

  buildHexField(field, motionBase, idle);

  const enableWideGateTransition = () => {
    requestAnimationFrame(() => {
      kvRoot.classList.add("is-wide-gated");
    });
  };

  const onWideSync = () => syncWideHexVisibility(kvRoot);
  window.addEventListener("resize", onWideSync);
  window.visualViewport?.addEventListener("resize", onWideSync);
  onWideSync();
  requestAnimationFrame(onWideSync);

  let loadingTimer = 0;
  let uiRevealTimer = 0;

  const finishReady = ({ recordIntroPlayed = false } = {}) => {
    setKvPhaseClasses(kvRoot, "is-kv-ready");
    field?.setAttribute("aria-hidden", "false");
    kvRoot.querySelectorAll(".kv__pulse").forEach((el) => {
      el.setAttribute("aria-hidden", "true");
    });
    onWideSync();
    enableWideGateTransition();
    if (recordIntroPlayed) markKvIntroPlayed();
    onReady?.();
  };

  const playKvLoading = () => {
    window.clearTimeout(loadingTimer);
    window.clearTimeout(uiRevealTimer);

    if (reducedMotion.matches) {
      enableFallback();
      finishReady({ recordIntroPlayed: true });
      return;
    }

    setKvPhaseClasses(kvRoot, "is-kv-loading");
    field?.setAttribute("aria-hidden", "true");
    void loader?.offsetWidth;

    loadingTimer = window.setTimeout(() => {
      onWideSync();
      setKvPhaseClasses(kvRoot, "is-kv-revealing");

      uiRevealTimer = window.setTimeout(() => {
        finishReady({ recordIntroPlayed: true });
      }, UI_REVEAL_DURATION_MS);
    }, LOADING_DURATION_MS);
  };

  if (shouldSkipKvOpening()) {
    kvRoot.classList.add("is-kv-skip-motion");
    finishReady();
    preloadMotionImages(
      motionBase,
      HEX_ITEMS.map(({ id }) => id),
      PRELOAD_TIMEOUT_MS,
    ).catch(
      () => {},
    );
  } else {
    preloadMotionImages(
      motionBase,
      HEX_ITEMS.map(({ id }) => id),
      PRELOAD_TIMEOUT_MS,
    )
      .catch(() => {})
      .finally(playKvLoading);
  }

  return () => {
    window.clearTimeout(loadingTimer);
    window.clearTimeout(uiRevealTimer);
    window.removeEventListener("resize", onWideSync);
    window.visualViewport?.removeEventListener("resize", onWideSync);
  };
};
